import { useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// value: true | false | null  -> "si" | "no" | "sc"
export function YesNoSelect({ value, onChange, testId }) {
  const str = value === true ? "si" : value === false ? "no" : "sc";
  return (
    <Select
      value={str}
      onValueChange={(v) => onChange(v === "si" ? true : v === "no" ? false : null)}
    >
      <SelectTrigger data-testid={testId} className="rounded-xl w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="sc">Sin contestar</SelectItem>
        <SelectItem value="si">Sí</SelectItem>
        <SelectItem value="no">No</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function Field({ label, children, hint, error, errorTestId }) {
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-slate-600 text-sm">{label}</Label>}
      {children}
      {error ? (
        <p data-testid={errorTestId} className="text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-xs text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Fechas y horarios (CONSOLA-01)
// `input type=date|time` SÓLO representa 'YYYY-MM-DD' / 'HH:MM'. Un valor guardado en otro formato
// se veía VACÍO (representación incorrecta, el dato seguía en la base). Estas utilidades lo
// normalizan para mostrarlo; si no se puede interpretar devuelven "" pero NO tocan el dato.
export function toDateInput(v) {
  if (!v) return "";
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    const dia = a > 12 ? a : b > 12 ? b : a;
    const mes = a > 12 ? b : b > 12 ? a : b;
    if (mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31) {
      return `${m[3]}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
    }
  }
  return "";
}

export function toTimeInput(v) {
  if (!v) return "";
  const s = String(v).trim();
  let m = /^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap])\.?\s*m\.?$/i.exec(s);
  if (m) {
    // Misma validación que el backend (`hora_hhmm`): la hora de 12 horas se comprueba ANTES de
    // convertir, así '25:30 a.m.' no se vuelve '01:30'. 12 a.m. -> 00:00, 12 p.m. -> 12:00.
    const h12 = Number(m[1]);
    const mi = Number(m[2]);
    if (h12 < 1 || h12 > 12 || mi > 59) return "";
    let h = h12 % 12;
    if (m[3].toLowerCase() === "p") h += 12;
    return `${String(h).padStart(2, "0")}:${m[2]}`;
  }
  m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s);
  if (m && Number(m[1]) < 24 && Number(m[2]) < 60) {
    return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
  }
  return "";
}

// Control compartido de fecha/hora. Mientras la captura está a medias el navegador emite "": ese
// vacío TRANSITORIO ya no borra el valor guardado. Al salir del control se distinguen DOS casos:
//  - CAPTURA INCOMPLETA o inválida (el navegador marca validity.badInput y el control queda con
//    segmentos a medias): el valor guardado se CONSERVA y el control vuelve a mostrarlo.
//  - BORRADO INTENCIONAL (el campo quedó realmente vacío, sin entrada pendiente): se propaga "".
function CampoTemporal({ tipo, normalizar, value, onChange, testId, className, ariaLabel }) {
  const [borrador, setBorrador] = useState(null);
  const canonico = normalizar(value);
  const mostrado = borrador !== null ? borrador : canonico;
  return (
    <Input
      type={tipo}
      data-testid={testId}
      aria-label={ariaLabel}
      className={className || "rounded-xl"}
      value={mostrado}
      onChange={(e) => {
        const v = e.target.value;
        setBorrador(v);
        if (v) onChange(v);
      }}
      onBlur={(e) => {
        // R55: el veredicto se toma con el ESTADO ACTUAL del control, no con un flag arrastrado de
        // un onChange anterior. Al borrar por segmentos el navegador deja `value=""` y puede NO
        // emitir otro `input` (el valor ya era ""), así que el flag `incompleto` quedaba pegado del
        // intento previo y el BORRADO EXPLÍCITO nunca se propagaba: el guardado reportaba éxito y
        // el valor anterior reaparecía tras recargar.
        const pendiente = e.target.validity?.badInput === true;
        if (e.target.value === "" && canonico && !pendiente) onChange("");
        setBorrador(null);
      }}
    />
  );
}

export function DateField(props) {
  return <CampoTemporal tipo="date" normalizar={toDateInput} {...props} />;
}

export function TimeField(props) {
  return <CampoTemporal tipo="time" normalizar={toTimeInput} {...props} />;
}

// Campo híbrido: seleccionar un contacto registrado O capturar persona aparte.
// FUNC-001: el MODO es estado propio del control. Antes se derivaba del valor, así que al elegir
// "Usar un contacto registrado" (que arranca sin id) el control volvía solo a "Sin definir" y el
// selector de contacto nunca aparecía: el titular del protocolo quedaba imposible de capturar.
export function HybridSelect({ value, onChange, contactos, testId }) {
  const v = value || { contacto_id: null, nombre: "", telefono: "" };
  const modoDelValor = v.contacto_id ? "contacto" : v.nombre || v.telefono ? "otra" : "ninguno";
  const [modo, setModo] = useState(modoDelValor);
  const [modoSync, setModoSync] = useState(modoDelValor);

  // Si el valor llega desde fuera (carga de la ficha), el modo se re-sincroniza una sola vez.
  if (modoDelValor !== modoSync) {
    setModoSync(modoDelValor);
    if (modoDelValor !== "ninguno" || modo === "ninguno") setModo(modoDelValor);
  }

  const cambiarModo = (m) => {
    setModo(m);
    if (m !== modo) onChange({ contacto_id: null, nombre: "", telefono: "" });
  };

  return (
    <div className="space-y-2">
      <Select value={modo} onValueChange={cambiarModo}>
        <SelectTrigger data-testid={testId} className="rounded-xl">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ninguno">Sin definir</SelectItem>
          <SelectItem value="contacto">Usar un contacto registrado</SelectItem>
          <SelectItem value="otra">Capturar otra persona</SelectItem>
        </SelectContent>
      </Select>

      {modo === "contacto" && (
        <Select
          value={v.contacto_id || ""}
          onValueChange={(id) => onChange({ contacto_id: id, nombre: "", telefono: "" })}
        >
          <SelectTrigger data-testid={testId ? `${testId}-contacto` : undefined} className="rounded-xl">
            <SelectValue placeholder="Selecciona un contacto" />
          </SelectTrigger>
          <SelectContent>
            {(contactos || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.nombre_completo || "(sin nombre)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {modo === "otra" && (
        <div className="grid grid-cols-2 gap-2">
          <Input
            className="rounded-xl"
            placeholder="Nombre"
            data-testid={testId ? `${testId}-nombre` : undefined}
            value={v.nombre || ""}
            onChange={(e) => onChange({ ...v, contacto_id: null, nombre: e.target.value })}
          />
          <Input
            className="rounded-xl"
            placeholder="Teléfono"
            data-testid={testId ? `${testId}-telefono` : undefined}
            value={v.telefono || ""}
            onChange={(e) => onChange({ ...v, contacto_id: null, telefono: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
